import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Calendar, TrendingUp, Clock } from "lucide-react";
import { format } from "date-fns";

export default function ArticlesList() {
  const navigate = useNavigate();

  const { data: articles, isLoading } = useQuery({
    queryKey: ['articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('publish_date', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    }
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                <div className="h-4 bg-muted rounded w-full" />
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Crypto Analysis & Research</h1>
        <p className="text-muted-foreground">
          AI-powered cryptocurrency intelligence for Australian traders
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {articles?.map((article) => (
          <Card 
            key={article.id}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/articles/${article.slug}`)}
          >
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                {article.featured && (
                  <Badge variant="default">Featured</Badge>
                )}
                <Badge variant="outline">{article.category}</Badge>
              </div>
              <CardTitle className="line-clamp-2">{article.headline}</CardTitle>
              <CardDescription className="line-clamp-3">
                {article.deck || article.summary}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(article.publish_date), 'MMM d, yyyy')}
                </div>
                {article.reading_time && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {article.reading_time} min read
                  </div>
                )}
              </div>
              {article.opportunity_score && (
                <div className="flex items-center gap-2 mt-3">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">
                    Score: {article.opportunity_score}/100
                  </span>
                </div>
              )}
              {article.tokens_featured && article.tokens_featured.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {article.tokens_featured.slice(0, 3).map((token) => (
                    <Badge key={token} variant="secondary" className="text-xs">
                      {token}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
